import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useInstance } from '../../contexts/InstanceContext';
import { getLogo, getBusinessName } from '../../lib/logos';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { LanguageSelector } from './LanguageSelector';
import NotificationCenter from '../ui/NotificationCenter';
import SupportModal from '../ui/SupportModal';
import { ConnectionIndicator, OfflineBanner } from '../ui/ConnectionIndicator';
import Sidebar from './Sidebar';
import MobileSidebar from './MobileSidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  ShoppingCart,
  Sun,
  Moon,
  LogOut,
  Menu,
  ChevronDown,
  Crown,
  Wallet,
  HelpCircle,
  Monitor,
} from 'lucide-react';

export default function MainLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isAdminMaster, currentInstance } = useInstance();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const logoSrc = getLogo(currentInstance, 'iconDark');
  const businessName = getBusinessName(currentInstance);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // PDV has its own layout
  const isPDV = location.pathname.startsWith('/pdv');
  if (isPDV) {
    return <Outlet />;
  }

  // Terminal has its own layout
  const isTerminal = location.pathname.startsWith('/terminal');
  if (isTerminal) {
    return <Outlet />;
  }

  // Calculate main content margin based on sidebar state
  // Rail: 80px, Drawer: 240px (when pinned/expanded)
  const mainMarginLeft = sidebarExpanded ? 'lg:ml-[320px]' : 'lg:ml-[80px]';
  // Header always stays at Rail position (drawer is below header)
  const headerLeft = 'lg:left-[80px]';

  return (
    <div className="min-h-screen bg-background">
      {/* Offline Banner - Shows at very top when offline */}
      <OfflineBanner />
      
      {/* Desktop Sidebar (Rail + Drawer) */}
      <div className="hidden lg:block">
        <Sidebar onExpandChange={setSidebarExpanded} />
      </div>

      {/* Mobile Sidebar */}
      <MobileSidebar 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)}
        onOpen={() => setMobileMenuOpen(true)}
      />

      {/* Fixed Header */}
      <header className={cn(
        'fixed top-0 left-0 right-0 h-14 bg-card border-b border-border z-40 transition-all duration-200',
        headerLeft
      )}>
        <div className="h-full flex items-center justify-between px-4">
          {/* Left side - Mobile menu + Logo + Quick Access */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Mobile Logo */}
            <Link to="/" className="lg:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg overflow-hidden">
                <img 
                  src={logoSrc} 
                  alt={businessName} 
                  className="w-full h-full object-contain"
                  data-testid="header-logo"
                />
              </div>
              <span className="font-bold">{businessName}</span>
            </Link>
            
            {/* Quick Access Buttons */}
            <div className="hidden sm:flex items-center gap-2 lg:ml-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/pdv')}
                className="gap-2"
                data-testid="header-pdv-btn"
              >
                <ShoppingCart className="h-4 w-4" />
                <span className="hidden md:inline">PDV</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/terminal')}
                className="gap-2"
                data-testid="header-terminal-btn"
              >
                <Monitor className="h-4 w-4" />
                <span className="hidden md:inline">Terminal</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/cashiers')}
                className="gap-2"
                data-testid="header-cashiers-btn"
              >
                <Wallet className="h-4 w-4" />
                <span className="hidden md:inline">Caixas</span>
              </Button>
            </div>
            
            {/* Instance name for Admin Master */}
            {isAdminMaster && currentInstance && (
              <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground ml-4">
                <Crown className="h-4 w-4 text-primary" />
                <span>{currentInstance.name}</span>
              </div>
            )}
          </div>

          {/* Right side - User menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Connection Status Indicator */}
            <ConnectionIndicator />

            {/* Support Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSupport(true)}
              data-testid="support-btn"
              title="Suporte"
              className="hidden sm:flex"
            >
              <HelpCircle className="h-5 w-5" />
            </Button>

            {/* Notifications */}
            <NotificationCenter />

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* Language */}
            <div className="hidden md:block">
              <LanguageSelector />
            </div>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium truncate max-w-[120px]">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 border-b">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <div className="md:hidden p-2 border-b">
                  <LanguageSelector />
                </div>
                <DropdownMenuItem onClick={() => setShowSupport(true)} className="sm:hidden">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Suporte
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                  {theme === 'dark' ? t('settings.themes.light') : t('settings.themes.dark')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={cn(
        'pt-14 min-h-screen transition-all duration-200',
        mainMarginLeft
      )}>
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>

      {/* Support Modal */}
      <SupportModal open={showSupport} onOpenChange={setShowSupport} />
    </div>
  );
}
